import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createTaskCategory, updateTaskCategory, deleteTaskCategory } from '../../Requests/TaskRequests';
import { getTeamUuid } from '../../utils/cookies';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon, PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { SwatchIcon } from '@heroicons/react/20/solid';
import ConfirmationModal from '../ConfirmationModal';

const PREDEFINED_COLORS = [
    '#FCA5A5', // Pastel Red
    '#FBD5B5', // Pastel Orange
    '#FDE68A', // Pastel Amber
    '#FEF3C7', // Pastel Yellow
    '#D9F99D', // Pastel Lime
    '#BBF7D0', // Pastel Green
    '#A7F3D0', // Pastel Emerald
    '#A5F3FC', // Pastel Cyan
    '#93C5FD', // Pastel Blue
    '#A5B4FC', // Pastel Indigo
    '#C4B5FD', // Pastel Violet
    '#DDD6FE', // Pastel Purple
    '#F9A8D4', // Pastel Pink
    '#FBBF24', // Pastel Rose/Gold
    '#D1D5DB', // Light Gray
    '#9CA3AF'  // Medium Gray
];

function CategoryManager({ open, onClose, categories, onCategoriesUpdate }) {
    const { t } = useTranslation();
    const [localCategories, setLocalCategories] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [formData, setFormData] = useState({ 
        name: '', 
        color: '#D1D5DB', 
        start_date: '', 
        end_date: '' 
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        setLocalCategories(categories || []);
    }, [categories]);

    const resetForm = () => {
        setFormData({ name: '', color: '#D1D5DB', start_date: '', end_date: '' });
        setEditingCategory(null);
        setShowForm(false);
        setErrors({});
    };

    const validateDates = (startDate, endDate) => {
        if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
            return t('Tasks.CategoryDateRangeError');
        }
        return null;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const newData = { ...prev, [name]: value };
            
            // Clear date range error when dates change
            if ((name === 'start_date' || name === 'end_date') && errors.dateRange) {
                setErrors(prevErrors => {
                    const { dateRange, ...rest } = prevErrors;
                    return rest;
                });
            }
            
            return newData;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors({});

        // Validate date range
        const dateError = validateDates(formData.start_date, formData.end_date);
        if (dateError) {
            setErrors({ dateRange: [dateError] });
            setLoading(false);
            return;
        }

        try {
            const categoryData = {
                ...formData,
                team_id: getTeamUuid()
            };

            let result;
            if (editingCategory) {
                result = await updateTaskCategory(editingCategory.id, categoryData);
                const updatedCategories = localCategories.map(cat => 
                    cat.id === editingCategory.id ? result.data : cat
                );
                setLocalCategories(updatedCategories);
                onCategoriesUpdate(updatedCategories);
            } else {
                result = await createTaskCategory(categoryData);
                const newCategories = [...localCategories, result.data];
                setLocalCategories(newCategories);
                onCategoriesUpdate(newCategories);
            }

            resetForm();
        } catch (error) {
            console.error('Error saving category:', error);
            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors);
            } else {
                setErrors({ general: [error.response?.data?.message || t('Tasks.AnErrorOccurred')] });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (category) => {
        setFormData({
            name: category.name,
            color: category.color || '#6B7280',
            start_date: category.start_date || '',
            end_date: category.end_date || ''
        });
        setEditingCategory(category);
        setShowForm(true);
    };

    const handleDelete = async (category) => {
        if (!window.confirm(t('Tasks.ConfirmDeleteCategory'))) {
            return;
        }

        try {
            setLoading(true);
            await deleteTaskCategory(category.id);
            const updatedCategories = localCategories.filter(cat => cat.id !== category.id);
            setLocalCategories(updatedCategories);
            onCategoriesUpdate(updatedCategories);
        } catch (error) {
            console.error('Error deleting category:', error);
            alert(error.response?.data?.message || t('Tasks.FailedToDeleteCategory'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} className="relative z-50">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            
            <div className="fixed inset-0 z-10 overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                        <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                            <div className="flex items-center justify-between mb-4">
                                <DialogTitle as="h3" className="text-base font-semibold leading-6 text-gray-900">
                                    {t('Tasks.ManageCategories')}
                                </DialogTitle>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                                >
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                            </div>

                            {/* Category List */}
                            <div className="space-y-2 max-h-60 overflow-y-auto mb-4">
                                {localCategories.length === 0 ? (
                                    <div className="text-center py-6 text-gray-500">
                                        {t('Tasks.NoCategories')}
                                    </div>
                                ) : (
                                    localCategories.map((category) => (
                                        <div key={category.id} className="flex items-center justify-between p-3 border rounded-lg">
                                            <div className="flex items-center space-x-3">
                                                <div 
                                                    className="w-4 h-4 rounded-full"
                                                    style={{ backgroundColor: category.color }}
                                                />
                                                <div>
                                                    <span className="text-sm font-medium text-gray-900">
                                                        {category.name}
                                                    </span>
                                                    {(category.start_date || category.end_date) && (
                                                        <div className="text-xs text-gray-500">
                                                            {category.start_date && category.end_date ? (
                                                                `${new Date(category.start_date).toLocaleDateString()} - ${new Date(category.end_date).toLocaleDateString()}`
                                                            ) : category.start_date ? (
                                                                `${t('Tasks.CategoryActiveFrom')}: ${new Date(category.start_date).toLocaleDateString()}`
                                                            ) : (
                                                                `${t('Tasks.CategoryActiveTo')}: ${new Date(category.end_date).toLocaleDateString()}`
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    onClick={() => handleEdit(category)}
                                                    className="text-gray-400 hover:text-gray-600"
                                                    disabled={loading}
                                                >
                                                    <PencilIcon className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(category)}
                                                    className="text-gray-400 hover:text-red-600"
                                                    disabled={loading}
                                                >
                                                    <TrashIcon className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>

                            {/* Add New Button */}
                            {!showForm && (
                                <button
                                    onClick={() => setShowForm(true)}
                                    className="w-full flex items-center justify-center p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-400 hover:text-gray-600 hover:border-gray-400"
                                >
                                    <PlusIcon className="h-5 w-5 mr-2" />
                                    {t('Tasks.AddCategory')}
                                </button>
                            )}

                            {/* Category Form */}
                            {showForm && (
                                <form onSubmit={handleSubmit} className="space-y-4 p-4 border rounded-lg bg-gray-50">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {t('Tasks.CategoryName')}
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-iavisus-100 focus:border-iavisus-100"
                                            placeholder={t('Tasks.CategoryNamePlaceholder')}
                                            required
                                        />
                                        {errors.name && (
                                            <p className="mt-1 text-sm text-red-600">{errors.name[0]}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('Tasks.CategoryColor')}
                                        </label>
                                        <div className="flex flex-wrap gap-2">
                                            {PREDEFINED_COLORS.map((color) => (
                                                <button
                                                    key={color}
                                                    type="button"
                                                    onClick={() => setFormData({ ...formData, color })}
                                                    className={`w-8 h-8 rounded-full border-2 ${
                                                        formData.color === color ? 'border-iavisus-100' : 'border-gray-300'
                                                    } hover:border-iavisus-100`}
                                                    style={{ backgroundColor: color }}
                                                />
                                            ))}
                                        </div>
                                    </div>

                                    {/* Date Range */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('Tasks.CategoryDates')}
                                        </label>
                                        <p className="text-xs text-gray-500 mb-3">{t('Tasks.CategoryDatesOptional')}</p>
                                        
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    {t('Tasks.CategoryStartDate')}
                                                </label>
                                                <input
                                                    type="date"
                                                    name="start_date"
                                                    value={formData.start_date}
                                                    onChange={handleInputChange}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-iavisus-100 focus:border-iavisus-100 text-sm"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    {t('Tasks.CategoryEndDate')}
                                                </label>
                                                <input
                                                    type="date"
                                                    name="end_date"
                                                    value={formData.end_date}
                                                    onChange={handleInputChange}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-iavisus-100 focus:border-iavisus-100 text-sm"
                                                />
                                            </div>
                                        </div>
                                        
                                        {errors.dateRange && (
                                            <p className="mt-1 text-sm text-red-600">{errors.dateRange[0]}</p>
                                        )}
                                    </div>

                                    {errors.general && (
                                        <div className="text-sm text-red-600">{errors.general[0]}</div>
                                    )}

                                    <div className="flex space-x-2">
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="flex-1 bg-iavisus-100 text-white px-4 py-2 rounded-md hover:bg-iavisus-200 focus:outline-none focus:ring-2 focus:ring-iavisus-100 disabled:opacity-50"
                                        >
                                            {loading ? t('Common.Saving') : (editingCategory ? t('Common.Update') : t('Common.Create'))}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={resetForm}
                                            className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                                        >
                                            {t('Common.Cancel')}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}

export default CategoryManager;